import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import * as analyticsApi from '../../api/analytics';

/**
 * FieldMappingTable - Interactive table for mapping file columns to CRM fields
 *
 * Features:
 * - Display source columns from uploaded file
 * - Select target CRM fields via dropdown
 * - Auto-mapping with confidence indicators
 * - Manual mapping override
 * - Static value input for unmapped fields
 * - Save/load mappings
 */
function FieldMappingTable({
  fileId,
  objectName,
  sourceType = 'salesforce',
  dataSourceId,
  onMappingsChange
}) {
  const { activeTenant } = useSelector((state) => state.auth);

  // State
  const [filePreview, setFilePreview] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoMapping, setAutoMapping] = useState(false);
  const [error, setError] = useState(null);
  const [savingMappings, setSavingMappings] = useState(false);

  // Load file preview and available fields
  useEffect(() => {
    if (!fileId || !objectName) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load file preview to get column headers
        const preview = await analyticsApi.getFilePreview(fileId);
        setFilePreview(preview);

        // Load available CRM fields for the object
        const fields = await analyticsApi.fetchFieldsByObject(objectName);
        setAvailableFields(fields.fields || []);

        // Try to load existing mappings
        try {
          const existingMappings = await analyticsApi.listFieldMappings(fileId);
          if (existingMappings && existingMappings.length > 0) {
            setMappings(existingMappings);
          } else {
            // Initialize empty mappings for each column
            const emptyMappings = preview.column_headers.map(col => ({
              source_column: col,
              target_field: '',
              transformation: 'VALUE_COPY',
              static_value: '',
              confidence_score: 0,
              is_auto_mapped: false,
            }));
            setMappings(emptyMappings);
          }
        } catch (err) {
          // No existing mappings, initialize empty
          const emptyMappings = preview.column_headers.map(col => ({
            source_column: col,
            target_field: '',
            transformation: 'VALUE_COPY',
            static_value: '',
            confidence_score: 0,
            is_auto_mapped: false,
          }));
          setMappings(emptyMappings);
        }
      } catch (err) {
        console.error('Failed to load field mapping data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fileId, objectName]);

  // Notify parent when mappings change
  useEffect(() => {
    if (onMappingsChange && mappings.length > 0) {
      onMappingsChange(mappings);
    }
  }, [mappings, onMappingsChange]);

  // Handle auto-mapping
  const handleAutoMap = async () => {
    if (!dataSourceId) {
      setError('Data source ID is required for auto-mapping');
      return;
    }

    setAutoMapping(true);
    setError(null);

    try {
      const response = await analyticsApi.autoMapFields(
        fileId,
        objectName,
        sourceType,
        dataSourceId
      );

      const suggestions = response.suggestions || [];

      // Merge suggestions with existing mappings
      const updatedMappings = mappings.map(mapping => {
        const suggestion = suggestions.find(s => s.source_column === mapping.source_column);
        if (suggestion && suggestion.target_field) {
          return {
            ...mapping,
            target_field: suggestion.target_field,
            confidence_score: suggestion.confidence_score,
            is_auto_mapped: true,
          };
        }
        return mapping;
      });

      setMappings(updatedMappings);
    } catch (err) {
      console.error('Auto-mapping failed:', err);
      setError(err.response?.data?.message || err.message || 'Auto-mapping failed');
    } finally {
      setAutoMapping(false);
    }
  };

  // Handle manual field selection
  const handleFieldChange = (sourceColumn, targetField) => {
    setMappings(prev => prev.map(mapping => {
      if (mapping.source_column === sourceColumn) {
        return {
          ...mapping,
          target_field: targetField,
          is_auto_mapped: false,
          confidence_score: targetField ? 1.0 : 0,
        };
      }
      return mapping;
    }));
  };

  // Handle static value input
  const handleStaticValueChange = (sourceColumn, value) => {
    setMappings(prev => prev.map(mapping => {
      if (mapping.source_column === sourceColumn) {
        return {
          ...mapping,
          static_value: value,
          transformation: value ? 'STATIC_VALUE' : 'VALUE_COPY',
        };
      }
      return mapping;
    }));
  };

  // Save mappings to backend
  const handleSaveMappings = async () => {
    setSavingMappings(true);
    setError(null);

    try {
      // Delete old mappings and create new ones
      // (In a real implementation, you'd use bulk upsert)
      for (const mapping of mappings) {
        if (mapping.target_field || mapping.static_value) {
          await analyticsApi.createFieldMapping({
            file_id: fileId,
            source_column: mapping.source_column,
            target_field: mapping.target_field,
            transformation: mapping.transformation,
            static_value: mapping.static_value,
            confidence_score: mapping.confidence_score,
            is_auto_mapped: mapping.is_auto_mapped,
          });
        }
      }
    } catch (err) {
      console.error('Failed to save mappings:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save mappings');
    } finally {
      setSavingMappings(false);
    }
  };

  // Get confidence indicator color
  const getConfidenceColor = (score) => {
    if (score >= 0.9) return 'text-green-600 bg-green-50';
    if (score >= 0.7) return 'text-yellow-600 bg-yellow-50';
    if (score >= 0.5) return 'text-orange-600 bg-orange-50';
    return 'text-red-600 bg-red-50';
  };

  // Get mapping status
  const getMappingStatus = (mapping) => {
    if (mapping.static_value) return { text: 'Static', color: 'blue' };
    if (mapping.target_field && mapping.is_auto_mapped) return { text: 'Auto', color: 'green' };
    if (mapping.target_field) return { text: 'Manual', color: 'purple' };
    return { text: 'Unmapped', color: 'gray' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="ml-3 text-gray-600">Loading field mappings...</p>
      </div>
    );
  }

  if (error && !mappings.length) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        {error}
      </div>
    );
  }

  const mappedCount = mappings.filter(m => m.target_field || m.static_value).length;
  const unmappedCount = mappings.length - mappedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Field Mapping</h3>
          <p className="text-sm text-gray-500 mt-1">
            Map file columns to {objectName} fields
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm">
            <span className="font-medium text-green-600">{mappedCount} mapped</span>
            <span className="text-gray-400 mx-2">•</span>
            <span className="font-medium text-red-600">{unmappedCount} unmapped</span>
          </div>
          <button
            onClick={handleAutoMap}
            disabled={autoMapping || !dataSourceId}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {autoMapping ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Auto-mapping...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Auto-Map
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Mapping table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-900 w-1/4">
                  Source Column
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-900 w-1/4">
                  Target Field
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-900 w-1/4">
                  Static Value
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-900 w-24">
                  Status
                </th>
                <th className="px-4 py-3 text-center font-medium text-gray-900 w-24">
                  Confidence
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {mappings.map((mapping, idx) => {
                const status = getMappingStatus(mapping);
                return (
                  <tr key={idx} className="hover:bg-gray-50">
                    {/* Source Column */}
                    <td className="px-4 py-3 text-gray-900 font-medium">
                      {mapping.source_column}
                    </td>

                    {/* Target Field Dropdown */}
                    <td className="px-4 py-3">
                      <select
                        value={mapping.target_field}
                        onChange={(e) => handleFieldChange(mapping.source_column, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                      >
                        <option value="">-- Select field --</option>
                        {availableFields.map((field) => (
                          <option key={field.api_name} value={field.api_name}>
                            {field.label} ({field.api_name})
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Static Value Input */}
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        value={mapping.static_value || ''}
                        onChange={(e) => handleStaticValueChange(mapping.source_column, e.target.value)}
                        placeholder="Optional static value"
                        disabled={!!mapping.target_field}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm disabled:bg-gray-100 disabled:text-gray-500"
                      />
                    </td>

                    {/* Status Badge */}
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full bg-${status.color}-100 text-${status.color}-700`}>
                        {status.text}
                      </span>
                    </td>

                    {/* Confidence Score */}
                    <td className="px-4 py-3 text-center">
                      {mapping.confidence_score > 0 ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${getConfidenceColor(mapping.confidence_score)}`}>
                          {Math.round(mapping.confidence_score * 100)}%
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveMappings}
          disabled={savingMappings || mappedCount === 0}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {savingMappings ? 'Saving...' : 'Save Mappings'}
        </button>
      </div>

      {/* Legend */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Mapping Guide</h4>
        <div className="grid grid-cols-2 gap-3 text-sm text-gray-600">
          <div>
            <span className="font-medium">Auto:</span> Field automatically mapped by algorithm
          </div>
          <div>
            <span className="font-medium">Manual:</span> Field manually selected by you
          </div>
          <div>
            <span className="font-medium">Static:</span> Fixed value for all records
          </div>
          <div>
            <span className="font-medium">Confidence:</span> How confident the auto-mapper is (90%+ is high)
          </div>
        </div>
      </div>
    </div>
  );
}

export default FieldMappingTable;
